import { api } from "./api";
import { getAttributionForCheckout } from "./attribution";
import { getAttributionV2 } from "./utm";
import { trackEvent } from "./track";

/**
 * Initiates a Stripe Checkout session for the given product and redirects
 * the browser to the Stripe-hosted checkout page.
 *
 * Phase 2 revenue tracking:
 *   - Pushes normalized `src` / `campaign` / `product_type` into the
 *     Stripe metadata so the webhook handler can record them on the
 *     conversions row (source-of-truth confirmed revenue).
 *   - Fires `checkout_session_created` event the moment Stripe returns
 *     a usable session URL, BEFORE redirecting. This is the funnel
 *     marker for "intent that turned into a real session".
 *
 * @param {"book"|"blueprint"|"orientation_reset"} product
 * @param {{ name?: string, email?: string }} [customer]
 * @returns {Promise<void>} — resolves when redirect is triggered or throws
 */
export async function startCheckout(product, customer = {}) {
  const v2 = getAttributionV2();
  const payload = {
    product,
    origin_url: window.location.origin,
  };
  if (customer.name) payload.name = customer.name;
  if (customer.email) payload.email = customer.email;
  const attribution = {
    ...getAttributionForCheckout(),
    // Canonical normalized keys for the Phase 2 webhook → conversions
    // pipeline. The backend extracts these into the Stripe metadata
    // alongside the existing `attr_*` keys.
    src: v2.src,
    campaign: v2.campaign || "",
    product_type: product,
  };
  payload.attribution = attribution;

  const { data } = await api.post("/stripe/checkout-session", payload);
  if (!data || !data.url) {
    throw new Error("No checkout URL returned");
  }
  trackEvent("checkout_session_created", {
    product_type: product,
    session_id: data.session_id || null,
    src: v2.src,
    campaign: v2.campaign,
  });
  window.location.href = data.url;
}

/** Polls /stripe/checkout-status/{session_id} until paid/expired or max attempts. */
export async function pollCheckoutStatus(sessionId, {
  maxAttempts = 8,
  intervalMs = 2000,
  onUpdate,
} = {}) {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const { data } = await api.get(`/stripe/checkout-status/${sessionId}`);
      onUpdate?.(data, i);
      if (data.payment_status === "paid") return data;
      if (data.status === "expired") return data;
    } catch (err) {
      // Surface only on the final attempt
      if (i === maxAttempts - 1) throw err;
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return { payment_status: "pending", status: "polling_timeout" };
}
